import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import SectionHeader from "./shared/SectionHeader";

/**
 * DarkMediaChecklistSection — full-bleed dark section with split copy
 * (eyebrow + two-line title + checkmark bullet list + CTA + muted
 * footer line) on one side and a large media asset on the other.
 * Matches the "Sell without being online 24x7" AUTOMATE block on
 * graphy.com's marketing-tools page.
 */

export type DarkChecklistBullet = {
  text: string;
};

export type DarkMediaChecklistSectionData = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  bullets: DarkChecklistBullet[];
  ctas?: CtaType[];
  footerLine?: string;
  image?: string;
  imageAlt?: string;
  imagePosition?: "left" | "right";
};

const DarkMediaChecklistSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  bullets,
  ctas,
  footerLine,
  image,
  imageAlt,
  imagePosition = "right",
}: DarkMediaChecklistSectionData) => {
  const copyColumn = (
    <div className="flex flex-col">
      <SectionHeader
        eyebrow={eyebrow}
        titleMuted={titleMuted}
        titleBright={titleBright}
        title={title}
        variant="dark-lime"
        align="left"
      />
      <ul className="mt-8 space-y-4 md:mt-10">
        {bullets.map((b) => (
          <li key={b.text} className="flex items-start gap-3">
            <span className="mt-[2px] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brandPrimary text-white">
              <FontAwesomeIcon
                icon={faCheck}
                style={{ fontSize: 12 }}
                aria-hidden="true"
              />
            </span>
            <span className="font-inter text-[14px] leading-relaxed text-white/80 md:text-[15px]">
              {b.text}
            </span>
          </li>
        ))}
      </ul>
      {ctas && ctas.length ? (
        <div className="mt-8 flex md:mt-10">
          <CtaButtonGroup ctas={ctas} gap="gap-3" />
        </div>
      ) : null}
      {footerLine ? (
        <p className="mt-6 font-inter text-[13px] leading-relaxed text-white/50 md:text-[14px]">
          {footerLine}
        </p>
      ) : null}
    </div>
  );

  const mediaColumn = image ? (
    <div className="relative mx-auto flex h-full w-full min-h-[340px] items-stretch overflow-hidden rounded-[32px] md:min-h-[520px]">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={image}
        alt={imageAlt ?? ""}
        className="absolute inset-0 h-full w-full object-cover"
      />
    </div>
  ) : null;

  return (
    <section className="relative w-full overflow-hidden bg-neutralBlack">
      <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div
          className={classNames(
            "mx-auto grid max-w-6xl gap-10 md:items-center md:gap-16",
            image ? "md:grid-cols-2" : ""
          )}
        >
          {imagePosition === "left" && mediaColumn ? (
            <>
              {mediaColumn}
              {copyColumn}
            </>
          ) : (
            <>
              {copyColumn}
              {mediaColumn}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default DarkMediaChecklistSection;
